<template>
    <loading v-if="loading"></loading>
    <div v-else>
        <div id="widget_header" class="d-flex justify-content-between">
            <h1>{{widget.header_title}}</h1>
            <button type="button" class="close" aria-label="Close" onclick="window.parent.postMessage('productlift.close', '*')">
                <span aria-hidden="true">&times;</span>
            </button>
        </div>
        <div v-for="post in postsWithoutSection" :key="post.id" :id="`post-${post.id}`">

            <div class="padding">
                <div class="d-flex justify-content-between align-items-end w-100">
                    <div class="flex-grow-1 pr-2" style="word-wrap: break-word; min-width: 0; flex: 1;">

                        <status-badge v-if="postSettings.display_status" :status="post.status" size="sm" />

                        <category-badge v-if="postSettings.display_categories" v-for="category in post.categories" :key="category.id" :category="category" />
                    </div>
                    <p class="small text-muted text-nowrap m-0">{{post.latest_status_change_at_formatted}}</p>

                </div>

                <p class="font-weight-bold my-2" style="word-wrap: break-word;">
                    {{ post.title }}
                </p>
                <p class="flex-text pt-2 mb-2 small" v-html="post.clean_description_changelog"></p>
            </div>
            <hr>
        </div>
        <div v-if="sectionHasMore[null]" class="text-center mt-3">
            <b-button @click="loadMorePosts(null)" variant="outline" size="sm">{{ $t('common.load_more') }}</b-button>
        </div>
        <div class="d-flex justify-content-between padding mb-3">
            <a :href="widget.powered_by_link" v-if="!widget.has_whitelabel" target="_blank" class="small text-muted">Made with ProductLift</a>
            <a :href="widget.to_portal_url" target="_blank">See all updates</a>
        </div>
    </div>
</template>

<script>
import NotificationPop from '../mixins/NotificationPop.js'
import Loading from "./Loading.vue";
import { mapState } from 'vuex';
import TabHandlers
    from "../mixins/TabHandlers";
import CategoryBadge
    from "./CategoryBadge";
import StatusBadge
    from "./StatusBadge";


export default {
    components: {
        Loading,
        CategoryBadge,
        StatusBadge,
    },
    mixins: [NotificationPop, TabHandlers],
    data() {
        return {
            showNewSectionModal: false,
            windowWidth: window.innerWidth,
            loading: true,
            sections: [],
            statuses: [],
            column_names: 'section',
            postsWithoutSection: [],
            tab: {},
            categories: {},
            postSettings: {},
            selectedCategoryId: null,
            sectionLoading: {}, // Tracks loading state of each section
            sectionHasMore: {}, // See if we can load more
            currentPage: {},
            newStatusId: null,
            newSectionId: null,
            defaultNewStatusId: null,
            defaultNewSectionId: null,
            otherSection: {
                id: null,
                title: 'Other',
                is_hidden_for_users: false
            },
            expandedSearch: false,
            searchQuery: '',
            postsPerPage: 3, // Pagination
            dataSlug: null,
            widget: [],
        };
    },
    computed: {
        ...mapState(['isLoggedIn', 'isAdmin', 'newFeedbackButton']),
    },
    mounted() {
        this.loadWidgetInfo()
    },

    methods: {

        loadWidgetInfo() {
            const widgetId = this.$route && this.$route.query && this.$route.query.widget_id;
            if (!widgetId) {
                this.loading = false;
                alert('Cannot load widget, widget id is missing.');
                return;
            }

            return this.$http.get(`/http_api/widgets/${widgetId}`)
                .then(({ data }) => {
                    this.widget = data.data;
                    this.dataSlug = this.widget.tab_slug

                    this.init(); // Call the async init method
                })
                .catch(error => {
                    this.loading = false;
                    console.log(error);
                    this.popNotification('Something went wrong on our side. Could not load widget info. Please try again or contact support.', 'danger');
                });
        },

        /**
         *  Ideally, for a Kanban board, you should aim to minimize the number of requests. A single API request that fetches all necessary data (tab configuration, sections, posts within each section, and categories for filtering) is typically more efficient. This approach reduces the number of server round trips, leading to faster initial rendering of the Kanban board.
         *  We make two, because first we get all settings and such, then we load posts
         */
        loadTab() {
            return this.$http.get(`/http_api/tabs/${this.dataSlug}/fetch`)
                .then(({data}) => {
                    this.tab = data.tab
                    this.sections = data.sections
                    this.categories = data.categories
                    this.statuses = data.statuses
                    this.postSettings = this.tab
                    this.otherSection.tab_id = this.tab.id
                    this.otherSection.title = this.otherSectionTitle

                    // Keep the section if it's not hidden or if the user is an admin
                    this.sections = data.sections.filter(section => {
                        return !section.is_hidden_for_users || this.isAdmin;
                    });
                })
                .catch(error => {
                    this.loading = true;

                    let errorMessage = 'Something went wrong on our side. Could not load posts. Please try again or contact ruben@productlift.dev';
                    let errorType = 'danger';
                    let errorDetails = null;

                    if (error.response) {
                        // The request was made and the server responded with a status code
                        // that falls out of the range of 2xx
                        switch (error.response.status) {
                            case 404:
                                errorMessage = 'The requested resource was not found.';
                                errorType = 'warning';
                                break;
                            case 403:
                                errorMessage = 'You do not have permission to access this resource.';
                                break;
                            case 500:
                                errorMessage = 'An internal server error occurred. Our team has been notified.';
                                break;
                            default:
                                errorMessage = `An error occurred: ${error.response.status} ${error.response.statusText}`;
                        }
                        errorDetails = error.response.data;
                    } else if (error.request) {
                        // The request was made but no response was received
                        errorMessage = 'No response received from the server. Please check your internet connection.';
                    } else {
                        // Something happened in setting up the request that triggered an Error
                        errorMessage = 'An error occurred while setting up the request.';
                    }

                    console.error('Error details:', error);

                    this.popNotification(errorMessage, errorType);
                });
        },

        getResponsiveClass() {
            let mode = this.tab.responsive_mode
            return {
                'kanban_responsive_horizontal': mode === 'horizontal',
                'kanban_responsive_vertical': mode === 'vertical'
            };
        }

    },
}
</script>

<style scoped>
    .padding {
        padding: 0 15px;
    }
</style>